'use client'
import type { TooltipProps } from '@radix-ui/react-tooltip'
import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import type { ComponentPropsWithoutRef, ReactElement, ReactNode } from 'react'
import { useRef, useState } from 'react'
import CkEditorCSRRenderer from '@/components/elementary/CkEditorRenderer/CkEditorCSRRenderer'
import {
	Tooltip as RadixTooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@/components/ui/tooltip'
import { isHtmlString } from '@/lib/html'

interface Props {
	readonly children: ReactElement
	readonly content: ReactNode | string
	readonly contentProps?: Partial<ComponentPropsWithoutRef<typeof TooltipContent>>
	readonly open?: TooltipProps['open']
	readonly onOpenChange?: TooltipProps['onOpenChange']
}

export function Tooltip({ children, content, contentProps, open, onOpenChange }: Props) {
	const [isOpen, setIsOpen] = useState(open ?? false)
	const triggerRef = useRef<HTMLButtonElement>(null)

	const handleTriggerClick = () => setIsOpen((prev) => !prev)

	const handleOpenChange = (nextOpen: boolean) => {
		setIsOpen(nextOpen)
		onOpenChange?.(nextOpen)
	}

	return (
		<TooltipProvider>
			<RadixTooltip open={isOpen} onOpenChange={handleOpenChange} delayDuration={0}>
				<TooltipTrigger
					asChild
					ref={triggerRef}
					onClick={handleTriggerClick}
					style={{ cursor: 'pointer' }}
					name="tooltip-trigger"
				>
					{children}
				</TooltipTrigger>
				<TooltipPrimitive.TooltipPortal>
					<TooltipContent {...contentProps} className="max-w-[200px]">
						{typeof content === 'string' && isHtmlString(content) ? (
							<CkEditorCSRRenderer variant="textOnly" htmlContent={content} />
						) : (
							content
						)}
					</TooltipContent>
				</TooltipPrimitive.TooltipPortal>
			</RadixTooltip>
		</TooltipProvider>
	)
}
